import { SITE } from '@/app/constants';
import type { SiteType, TeamTier } from '@/app/types';

/** 企业版专属活动链接（咨询卡里"查看活动"会用到） */
export const ENTERPRISE_ACTIVITY_LINK: Record<SiteType, string> = {
  china: 'https://shengshu.feishu.cn/wiki/M7UfwCBbQiCFyek5N3yc5oEsn4d',
  global: 'https://shengshu.feishu.cn/wiki/CC5rwNUdSi9vsQkOprScxtSTnrF',
};

/** 企业版需求采集表链接 */
export const ENTERPRISE_GATHERING_FORM_LINK: Record<SiteType, string> = {
  china: 'https://shengshu.feishu.cn/share/base/form/shrcnrV2qUDXwvKcMWNW7Y96DCd',
  global: 'https://shengshu.feishu.cn/share/base/form/shrcn1WRhDqJI4cvEUEbnAjKPHc',
};

/** 销售经理联系方式（ConsultDialog 内显示） */
export const ENTERPRISE_SALES_MANAGER: Record<SiteType, { name: string; email: string; phone: string }> = {
  china: {
    name: '朱光月',
    email: 'guangyue.zhu@shengshu.ai',
    phone: '+86 18621692546',
  },
  global: {
    name: 'Guangyue Zhu',
    email: 'guangyue.zhu@shengshu.ai',
    phone: '+86 18621692546',
  },
};

/** 企业版品牌渐变（咨询卡 + 真实卡标题底色） */
export const TEAM_TITLE_BG: React.CSSProperties = {
  backgroundImage: 'linear-gradient(270deg, #15B8E6 0%, #ABE9FF 100%)',
};

export const TEAM_BUTTON_BG_STYLE: React.CSSProperties = {
  backgroundImage:
    SITE === 'china'
      ? 'url(https://image01.vidu.zone/vidu/media-asset/team_v2-87b6f375.webp)'
      : 'url(https://image01.cf.vidu.studio/vidu/media-asset/team_v2-87b6f375.webp)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  boxShadow: 'inset 0px 4px 10px 0px rgba(255,255,255,0.25), 0px 10px 40px 0px rgba(87,188,242,0.4)',
};

/**
 * 三档企业版的展示数字，按商业化策略文档对齐：
 * - basic（企业版）   ：300,000 积分 / 500 错峰 / 10 席位 / 1,000 主体 / 15 并发
 * - plus（企业版 Plus）：1,000,000 积分 / 1,500 错峰 / 30 席位 / 3,000 主体 / 25 并发
 * - pro（企业版 PRO） ：3,600,000 积分 / 3,000 错峰 / 100 席位 / 10,000 主体 / 50 并发
 */
export const TEAM_TIER_BENEFITS: Record<
  TeamTier,
  {
    /** 年积分 */
    credits: number;
    /** 错峰视频/天 */
    submission: number;
    /** 席位数 */
    seats: number;
    /** 月主体数 */
    subjects: number;
    /** 并发任务数 */
    concurrent: number;
  }
> = {
  basic: {
    credits: 300000,
    submission: 500,
    seats: 10,
    subjects: 1000,
    concurrent: 15,
  },
  plus: {
    credits: 1000000,
    submission: 1500,
    seats: 30,
    subjects: 3000,
    concurrent: 25,
  },
  pro: {
    credits: 3600000,
    submission: 3000,
    seats: 100,
    subjects: 10000,
    concurrent: 50,
  },
};

export type TeamBenefitKey =
  | 'team_benefit_channel'
  | 'team_benefit_credits_share'
  | 'team_benefit_manage'
  | 'team_benefit_support'
  | 'team_benefit_training'
  | 'team_benefit_account_manager'
  | 'team_benefit_solution'
  | 'team_benefit_new_feature'
  | 'team_benefit_q2_image_unlimited'
  | 'team_benefit_commercial_use'
  | 'team_benefit_credits'
  | 'team_benefit_seats'
  | 'team_benefit_submission'
  | 'team_benefit_concurrent'
  | 'team_benefit_subjects'
  | 'team_benefit_assets'
  | 'team_benefit_custom'
  | 'team_benefit_discount'
  | 'team_benefit_invoicing'
  | 'team_benefit_events';

/**
 * 各档"高级权益"列表（紫色 icon 样式）
 */
export const TEAM_TIER_HIGHLIGHTED_RIGHTS: TeamBenefitKey[] = [
  'team_benefit_credits',
  'team_benefit_seats',
  'team_benefit_submission',
  'team_benefit_concurrent',
  'team_benefit_subjects',
];

/** 各档"普通权益"列表（灰色 check 样式）。 */
export const TEAM_TIER_COMMON_RIGHTS: TeamBenefitKey[] = [
  'team_benefit_channel',
  'team_benefit_credits_share',
  'team_benefit_manage',
  'team_benefit_support',
  'team_benefit_training',
  'team_benefit_account_manager',
  'team_benefit_solution',
  'team_benefit_new_feature',
  'team_benefit_q2_image_unlimited',
  'team_benefit_commercial_use',
];

// 咨询占位卡的权益项
export const CONSULT_BENEFITS: TeamBenefitKey[] = [
  'team_benefit_manage',
  'team_benefit_assets',
  'team_benefit_channel',
  'team_benefit_invoicing',
  'team_benefit_events',
  'team_benefit_support',
  'team_benefit_training',
  'team_benefit_account_manager',
  'team_benefit_solution',
  'team_benefit_custom',
] as const;

/**
 * 咨询占位卡上的展示数字（"更高版本"咨询入口的占位）。
 */
export const CONSULT_PLACEHOLDER = {
  price: SITE === 'global' ? 2999 : 19800,
} as const;
